import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Bell, Settings, ChevronDown, Menu, Sun, Moon, Package, Plane, Ship, ArrowRight,
  Wallet, LogOut, User as UserIcon
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useWallet } from '../../context/WalletContext';
import { useNotifications } from '../../context/NotificationContext';
import { useTheme } from '../../context/ThemeContext';
import { getCloudFrontUrl } from '../../utils/s3Upload';

const chip = 'inline-flex items-center justify-center rounded-xl border transition-colors';
const quiet = 'border-slate-200 bg-white text-slate-500 hover:text-slate-700 hover:bg-slate-50 ' +
              'dark:border-slate-700/70 dark:bg-slate-800/60 dark:text-slate-400 dark:hover:text-slate-100 dark:hover:bg-slate-700/60';


// Notification payloads carry some fields as arrays and some as JSON strings,
// depending on whether they came from the SSE era or from FCM.
const parseField = (field) => {
  if (!field) return [];
  if (Array.isArray(field)) return field;
  if (typeof field === 'string') {
    try { const p = JSON.parse(field); return Array.isArray(p) ? p : [field]; } catch { return [field]; }
  }
  return [field];
};

const joinWithAnd = (value) => {
  const unique = [...new Set(parseField(value).filter((v) => v !== null && v !== undefined && v !== ''))];
  return unique.join(' & ') || '-';
};

/** Search, wallet, notifications and the account menu, above the page. */
const UserTopbar = ({ search = '', onSearch, onOpenMenu, suggestions = [], onPickSuggestion }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { balanceRupees } = useWallet();
  const { notifications = [], unreadCount = 0, markRead, markAllRead } = useNotifications();
  const { isDarkMode, toggleTheme } = useTheme();

  const [menuOpen, setMenuOpen] = useState(false);
  const [avatarBroke, setAvatarBroke] = useState(false);
  const [sugOpen, setSugOpen] = useState(false);
  const [sugIndex, setSugIndex] = useState(-1);
  const searchBoxRef = useRef(null);
  const [bellOpen, setBellOpen] = useState(false);
  const menuRef = useRef(null);
  const bellRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (bellRef.current && !bellRef.current.contains(e.target)) setBellOpen(false);
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) setSugOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  // ⌘K / Ctrl-K puts the cursor in the search box, as the hint in it promises.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const unread = notifications.filter((n) => !n.isRead);
  const visibleSuggestions = suggestions.slice(0, 8);

  const openNotification = (n) => {
    const data = n.data || {};
    markRead?.(n.id);
    setBellOpen(false);
    const isSea = data.shipmentType === 'Sea-Export';
    const id = isSea ? data.blNumber : (data.awbNo || data.awbNumber);
    if (!id) return;
    navigate(isSea ? `/sea-shipment/${id}` : `/shipment/${id}`);
  };
  const pick = (sg) => {
    setSugOpen(false);
    setSugIndex(-1);
    if (onPickSuggestion) onPickSuggestion(sg);
    else onSearch?.(sg.title);
  };

  return (
    <header className="sticky top-0 z-20 flex items-center gap-3 px-4 sm:px-6 h-[68px]
                       bg-white/85 dark:bg-[#0d1424]/85 backdrop-blur-xl
                       border-b border-slate-200/80 dark:border-slate-700/60">
      <button type="button" onClick={onOpenMenu}
        className={`${chip} ${quiet} lg:hidden w-10 h-10`} aria-label="Open menu">
        <Menu size={18} />
      </button>

      {/* search */}
      <div className="relative flex-1 max-w-[520px]" ref={searchBoxRef}>
        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          ref={searchRef}
          value={search}
          onChange={(e) => { onSearch?.(e.target.value); setSugOpen(true); setSugIndex(-1); }}
          onFocus={() => setSugOpen(true)}
          onKeyDown={(e) => {
            if (!visibleSuggestions.length) return;
            if (e.key === 'ArrowDown') { e.preventDefault(); setSugOpen(true); setSugIndex((i) => (i + 1) % visibleSuggestions.length); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setSugIndex((i) => (i <= 0 ? visibleSuggestions.length : i) - 1); }
            else if (e.key === 'Enter' && sugIndex >= 0) { e.preventDefault(); pick(visibleSuggestions[sugIndex]); }
            else if (e.key === 'Escape') { setSugOpen(false); setSugIndex(-1); }
          }}
          placeholder="Search by AWB, Shipment, Customer..."
          autoComplete="off"
          className="w-full h-11 pl-10 pr-14 rounded-xl text-sm
                     bg-slate-50 dark:bg-slate-800/70
                     border border-slate-200 dark:border-slate-700/70
                     text-slate-800 dark:text-slate-100 placeholder-slate-400
                     outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10 transition"
        />
        <kbd className="hidden sm:flex absolute right-2.5 top-1/2 -translate-y-1/2 items-center gap-0.5
                        px-1.5 h-6 rounded-md text-[11px] font-semibold
                        bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                        text-slate-400 pointer-events-none">⌘ K</kbd>

        {sugOpen && search.trim() && (
          <div className="absolute left-0 right-0 mt-2 max-h-[340px] overflow-y-auto rounded-2xl shadow-2xl z-30
                          bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700">
            {visibleSuggestions.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-slate-400">
                Nothing matches “{search.trim()}”.
              </p>
            ) : visibleSuggestions.map((sg, i) => (
              <button
                key={sg.id}
                type="button"
                onMouseEnter={() => setSugIndex(i)}
                onClick={() => pick(sg)}
                className={`w-full text-left px-4 py-2.5 flex items-center gap-3 transition
                  ${i === sugIndex ? 'bg-blue-50 dark:bg-slate-800' : 'hover:bg-slate-50 dark:hover:bg-slate-800/70'}`}
              >
                <span className="w-8 h-8 rounded-lg grid place-items-center flex-shrink-0
                                 bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300">
                  <Package size={15} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[13px] font-bold text-slate-800 dark:text-slate-100 truncate">{sg.title}</span>
                  <span className="block text-[11.5px] text-slate-500 dark:text-slate-400 truncate">{sg.subtitle}</span>
                </span>
                {sg.meta && (
                  <span className="text-[11.5px] font-semibold text-slate-400 whitespace-nowrap">{sg.meta}</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex-1" />

      {/* notifications */}
      <div className="relative" ref={bellRef}>
        <button type="button" onClick={() => { setBellOpen((v) => !v); setMenuOpen(false); }}
          className={`${chip} ${quiet} w-10 h-10`} aria-label="Notifications">
          <Bell size={18} />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full
                             bg-red-500 text-white text-[10px] font-bold grid place-items-center
                             ring-2 ring-white dark:ring-[#0d1424]">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>

        {bellOpen && (
          <div className="fixed sm:absolute left-2 right-2 sm:left-auto sm:right-0 top-[72px] sm:top-auto sm:mt-2
                          w-auto sm:w-[390px] max-h-[70vh] sm:max-h-[440px] flex flex-col overflow-hidden rounded-2xl shadow-2xl z-30
                          bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-800 flex-shrink-0">
              <p className="text-sm font-bold text-slate-900 dark:text-white">New Shipments ({unreadCount})</p>
              {unread.length > 0 && (
                <button onClick={markAllRead} className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline">
                  Clear All
                </button>
              )}
            </div>

            <div className="overflow-y-auto">
              {unread.length === 0 ? (
                <div className="px-4 py-10 text-center text-slate-400">
                  <Bell size={30} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No new shipments</p>
                </div>
              ) : unread.map((n, index) => {
                const data = n.data || {};
                const isSea = data.shipmentType === 'Sea-Export';
                const shipmentId = isSea ? data.blNumber : (data.awbNo || data.awbNumber);
                const origin = isSea ? (data.portCode || 'DEL') : (data.origin || 'DEL');
                const destination = isSea
                  ? (data.portOfDischargeCode || '-')
                  : (data.destination || data.destCode || data.destCity || '-');
                const weight = isSea ? (data.gwt || '-') : (data.grWeight || data.grossWeight || '-');
                const packages = isSea ? (data.pkg || '-') : (data.totalPkt || data.numberOfPackages || '-');
                const party = isSea
                  ? (data.exporterName || '-')
                  : (joinWithAnd(data.customer) !== '-' ? joinWithAnd(data.customer) : (data.customerName || '-'));
                const sbDate = (parseField(data.sbDate || data.sbDates)[0] || '-').split('T')[0].split(' ')[0];

                return (
                  <button
                    key={n.id || index}
                    onClick={() => openNotification(n)}
                    className="w-full px-4 py-3 text-left border-b border-slate-50 dark:border-slate-800/70 last:border-0
                               hover:bg-blue-50/60 dark:hover:bg-slate-800/60 transition group"
                  >
                    <div className="flex items-start gap-3">
                      <span className={`mt-0.5 p-2 rounded-xl flex-shrink-0 ${isSea
                        ? 'bg-cyan-100 text-cyan-600 dark:bg-cyan-500/15 dark:text-cyan-300'
                        : 'bg-blue-100 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300'}`}>
                        {isSea ? <Ship size={17} /> : <Plane size={17} />}
                      </span>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3">
                          <p className="text-[13px] font-bold text-slate-900 dark:text-white">
                            New {isSea ? 'Sea' : 'Air'} Shipment
                          </p>
                          <span className={`text-[11.5px] font-bold whitespace-nowrap ${isSea
                            ? 'text-cyan-600 dark:text-cyan-300' : 'text-blue-600 dark:text-blue-300'}`}>
                            {shipmentId || 'N/A'}
                          </span>
                        </div>

                        <p className="text-[11.5px] text-slate-600 dark:text-slate-400 mt-0.5 break-words">
                          <span className="font-semibold">{isSea ? 'Exporter:' : 'Consignee:'}</span> {party}
                        </p>
                        <p className="text-[11.5px] text-slate-600 dark:text-slate-400">
                          Pkt: {packages} · Gr. Wt.: {weight} kg
                        </p>

                        <div className="flex items-center justify-between gap-3 mt-0.5">
                          {!isSea && (
                            <p className="text-[11.5px] text-slate-600 dark:text-slate-400 truncate">
                              <span className="font-semibold">SB Date:</span> {sbDate}
                            </p>
                          )}
                          <span className="flex items-center gap-1.5 text-[11.5px] font-semibold text-slate-500 dark:text-slate-400 ml-auto">
                            {origin} <ArrowRight size={12} /> {destination}
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* wallet */}
      <button type="button" onClick={() => navigate('/user/wallet')}
        className="hidden sm:inline-flex items-center gap-2 h-10 px-3.5 rounded-xl border
                   border-emerald-200 bg-emerald-50 text-emerald-700 text-sm font-bold
                   hover:bg-emerald-100 transition
                   dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300 dark:hover:bg-emerald-500/20">
        <Wallet size={16} />
        ₹ {Number(balanceRupees || 0).toLocaleString('en-IN')}
      </button>

      <button type="button" onClick={toggleTheme}
        className={`${chip} ${quiet} w-10 h-10`} aria-label="Toggle theme">
        {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
      </button>

      {/* account */}
      <div className="relative" ref={menuRef}>
        <button type="button" onClick={() => { setMenuOpen((v) => !v); setBellOpen(false); }}
          className="flex items-center gap-2.5 h-10 pl-1 pr-2 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800/60 transition">
          {user?.profilePicture && !avatarBroke ? (
            <img
              src={user.profilePicture.startsWith('http') ? user.profilePicture : getCloudFrontUrl(user.profilePicture)}
              alt=""
              // A key that no longer resolves renders as an empty box, which
              // reads as "my picture vanished" — fall back to the mark.
              onError={() => setAvatarBroke(true)}
              className="w-9 h-9 rounded-full object-cover ring-2 ring-white dark:ring-slate-700"
            />
          ) : (
            <span className="w-9 h-9 rounded-full grid place-items-center bg-blue-600 text-white">
              <UserIcon size={17} />
            </span>
          )}
          <span className="hidden md:block text-sm font-bold text-slate-800 dark:text-slate-100 max-w-[130px] truncate">
            {user?.name || 'User'}
          </span>
          <ChevronDown size={15} className="hidden md:block text-slate-400" />
        </button>

        {menuOpen && (
          <div className="absolute right-0 mt-2 w-56 rounded-2xl shadow-2xl overflow-hidden
                          bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700">
            <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800">
              <p className="text-sm font-bold text-slate-900 dark:text-white truncate">{user?.name || 'User'}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{user?.email || user?.role}</p>
            </div>
            <button onClick={() => { setMenuOpen(false); navigate('/user/profile'); }}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-semibold text-slate-700 dark:text-slate-200
                         hover:bg-slate-50 dark:hover:bg-slate-800 transition">
              <UserIcon size={16} /> Profile
            </button>
            <button onClick={() => { setMenuOpen(false); navigate('/user/settings'); }}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-semibold text-slate-700 dark:text-slate-200
                         hover:bg-slate-50 dark:hover:bg-slate-800 transition">
              <Settings size={16} /> Settings
            </button>
            <button onClick={() => { if (user?.id) localStorage.setItem(`lastLogout_${user.id}`, new Date().toISOString()); logout(); }}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-semibold text-red-600 dark:text-red-400
                         hover:bg-red-50 dark:hover:bg-red-900/20 transition border-t border-slate-100 dark:border-slate-800">
              <LogOut size={16} /> Log out
            </button>
          </div>
        )}
      </div>
    </header>
  );
};

export default UserTopbar;
